import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface ReceiptData {
  amount: number;
  bowlerCount: number;
  pricePerBowler: number;
  paymentIntentId: string;
  paidAt: string;
  adminName: string;
  adminEmail: string;
  billingPeriod: {
    label: string;
    totalPaid: number;
  };
}

const PERIOD_KEY: Record<string, string> = {
  may: 'admin.billing.periodMay',
  september: 'admin.billing.periodSeptember',
  december: 'admin.billing.periodDecember',
};

const AdminBillingSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [error, setError] = useState('');
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const verify = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setError(t('admin.billing.verifyFailed'));
        setVerifying(false);
        return;
      }

      // Wait briefly for Stripe to process
      await new Promise(resolve => setTimeout(resolve, 1500));

      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const res = await fetch(`${apiUrl}/admin/billing/verify?session_id=${sessionId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        if (data.success) {
          setReceipt(data);
        } else {
          setError(data.error || t('admin.billing.verifyFailed'));
        }
      } catch {
        setError(t('admin.billing.verifyFailed'));
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [searchParams, t]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bowler Pro – Payment Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #111; }
            .receipt { max-width: 480px; margin: 0 auto; border: 1px solid #ccc; border-radius: 8px; padding: 24px; }
            .logo { font-size: 24px; font-weight: bold; text-align: center; margin-bottom: 8px; }
            .subtitle { text-align: center; color: #666; margin-bottom: 24px; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .row:last-child { border-bottom: none; }
            .label { color: #555; }
            .value { font-weight: 600; }
            .total .value { color: #16a34a; font-size: 18px; }
            .footer { text-align: center; color: #888; font-size: 12px; margin-top: 24px; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('admin.billing.verifying')}</h2>
          <p className="text-purple-200">{t('admin.billing.verifyingMessage')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-red-400/50 max-w-md w-full text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-white mb-3">{error}</h2>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition"
          >
            {t('admin.billing.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Success header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-3">🎉</div>
          <h1 className="text-3xl font-bold text-white">{t('admin.billing.successTitle')}</h1>
          <p className="text-purple-200 mt-1">{t('admin.billing.successSubtitle')}</p>
        </div>

        {/* Printable receipt */}
        {receipt && (
          <>
            <div ref={printRef}>
              <div className="receipt bg-white rounded-xl p-6 mb-6 text-gray-800">
                <div className="logo" style={{ fontSize: 22, fontWeight: 'bold', textAlign: 'center', marginBottom: 4 }}>
                  🎳 Bowler Pro
                </div>
                <div className="subtitle" style={{ textAlign: 'center', color: '#666', marginBottom: 20, fontSize: 14 }}>
                  {t('admin.billing.receiptTitle')}
                </div>

                {[
                  [t('admin.billing.receiptDate'), new Date(receipt.paidAt).toLocaleString()],
                  [t('admin.billing.receiptAdmin'), `${receipt.adminName} (${receipt.adminEmail})`],
                  [t('admin.billing.receiptPeriod'), t(PERIOD_KEY[receipt.billingPeriod?.label] || 'admin.billing.periodDecember')],
                  [t('admin.billing.receiptBowlers'), receipt.bowlerCount],
                  [t('admin.billing.receiptPricePerBowler'), `$${receipt.pricePerBowler}`],
                  [t('admin.billing.receiptPaymentId'), receipt.paymentIntentId],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium text-gray-800 text-right max-w-[60%] break-all">{value}</span>
                  </div>
                ))}

                <div className="flex justify-between py-3 mt-2 font-bold text-lg">
                  <span className="text-gray-700">{t('admin.billing.receiptAmountPaid')}</span>
                  <span className="text-green-600">${receipt.amount.toFixed(2)}</span>
                </div>

                <div className="text-center text-gray-400 text-xs mt-4">
                  bowlerpro.com — {new Date().getFullYear()}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handlePrint}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded-xl transition duration-200"
              >
                🖨️ {t('admin.billing.printReceipt')}
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-3 rounded-xl transition duration-200"
              >
                {t('admin.billing.backToDashboard')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminBillingSuccess;
